/**
 * Email Templates — the letters the office sends, editable here rather than in the code.
 *
 * What to say to a unit that has gone quiet is a question about a relationship, not a
 * string constant, and it changes. So the letters are data: named, edited, and one of them
 * marked the default so the send dialogue has something chosen already.
 *
 * Placeholders are the double-brace kind and the list comes from the Worker, so the panel
 * showing what may be used cannot drift from what actually gets substituted. Anything
 * unrecognised is left standing in the letter on purpose — a visible {{whatever}} is a
 * mistake somebody can see and fix, where a silently empty sentence is one they cannot.
 */
import { useState } from 'react'
import { PageHeader } from '../App'
import { api, useApi } from '../lib/api'
import {
  Card, Field, Badge, Modal, Notice, Spinner, Empty, QueryState, ConfirmModal, useToast,
} from '../components/ui'
import { useRelabel } from '../lib/terms'

const BLANK = { name: '', subject: '', body: '', is_default: false }

export default function EmailTemplates({ user }) {
  const t = useRelabel()
  const toast = useToast()
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(BLANK)
  const [busy, setBusy] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(null)

  const allowed = user.caps.manageSettings
  const query = useApi('email-templates', 'email-templates', { enabled: allowed })

  if (!allowed) {
    return (
      <PageHeader title={t('Email Templates')}>
        <Notice tone="warn" icon="lock" title="Not available to your role">
          These are the letters the archive sends in the organization&rsquo;s name, so they are
          kept to owners and administrators. You are signed in as{' '}
          <strong>{user.roleLabel}</strong>.
        </Notice>
      </PageHeader>
    )
  }

  const open = (row) => {
    setEditing(row ?? 'new')
    setForm(
      row
        ? { name: row.name, subject: row.subject, body: row.body, is_default: !!row.is_default }
        : BLANK
    )
  }

  const save = async () => {
    setBusy(true)
    try {
      const body = { ...form, is_default: form.is_default ? 1 : 0 }
      if (editing === 'new') await api('email-templates', { method: 'POST', body })
      else await api(`email-templates/${editing.id}`, { method: 'PUT', body })
      setEditing(null)
      query.refetch()
      toast.ok(editing === 'new' ? `“${form.name}” written` : `“${form.name}” saved`)
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    setBusy(true)
    try {
      await api(`email-templates/${confirmDelete.id}`, { method: 'DELETE' })
      setConfirmDelete(null)
      query.refetch()
      toast.ok(`“${confirmDelete.name}” deleted`)
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  const set = (patch) => setForm((f) => ({ ...f, ...patch }))

  return (
    <PageHeader
      title={t('Email Templates')}
      sub={t('The letters sent to partner units, in your own words')}
      actions={
        <button className="btn btn-sm btn-primary" onClick={() => open(null)}>
          + New Letter
        </button>
      }
    >
      <QueryState query={query}>
        {(d) => (
          <div className="col">
            <Notice icon="info" title="What may go in a letter">
              <p>
                These are filled in for each recipient when the letter is sent, so there is
                nothing to type by hand:
              </p>
              <div className="row wrap" style={{ gap: 8, marginTop: 6 }}>
                {d.placeholders.map((x) => (
                  <Badge key={x.key} tone="muted" title={x.what}>
                    {`{{${x.key}}}`}
                  </Badge>
                ))}
              </div>
              <p style={{ marginTop: 10 }}>
                Anything else in double braces is left in the letter exactly as written —
                visible, so it can be spotted and fixed, rather than quietly turning into a
                gap in a sentence.
              </p>
            </Notice>

            {!d.rows.length ? (
              <Empty title="No letters yet">
                Write one, and it can be sent from {t('Email Partner Units')}.
              </Empty>
            ) : (
              <div className="col">
                {d.rows.map((r) => (
                  <Card
                    key={r.id}
                    title={r.name}
                    sub={r.subject}
                    actions={
                      <>
                        {!!r.is_default && <Badge tone="ok">default</Badge>}
                        <button className="btn btn-sm" onClick={() => open(r)}>
                          Edit
                        </button>
                        <button className="btn btn-sm btn-danger" onClick={() => setConfirmDelete(r)}>
                          Delete
                        </button>
                      </>
                    }
                  >
                    <pre
                      className="small subtle"
                      style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}
                    >
                      {r.body}
                    </pre>
                    <p className="small subtle" style={{ margin: '10px 0 0' }}>
                      {r.updated_by ? `Last edited by ${r.updated_by}` : `Written by ${r.created_by}`}
                    </p>
                  </Card>
                ))}
              </div>
            )}

            <Modal
              open={!!editing}
              onClose={() => setEditing(null)}
              title={editing === 'new' ? 'A new letter' : `Edit “${editing?.name ?? ''}”`}
              footer={
                <>
                  <button
                    className="btn btn-primary"
                    onClick={save}
                    disabled={busy || !form.name.trim() || !form.subject.trim() || !form.body.trim()}
                  >
                    {busy ? <Spinner /> : 'Save letter'}
                  </button>
                  <button className="btn" onClick={() => setEditing(null)} disabled={busy}>
                    Cancel
                  </button>
                </>
              }
            >
              <div className="col">
                <Field label="Name" hint="How this letter is listed when one is chosen to send.">
                  <input
                    className="input"
                    value={form.name}
                    onChange={(e) => set({ name: e.target.value })}
                    placeholder="Request an update"
                  />
                </Field>
                <Field label="Subject" hint="Placeholders work here too.">
                  <input
                    className="input"
                    value={form.subject}
                    onChange={(e) => set({ subject: e.target.value })}
                  />
                </Field>
                <Field label="Letter">
                  <textarea
                    className="input"
                    rows={14}
                    value={form.body}
                    onChange={(e) => set({ body: e.target.value })}
                    style={{ fontFamily: 'inherit', lineHeight: 1.5 }}
                  />
                </Field>
                <label className="row row-tight small" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.is_default}
                    onChange={(e) => set({ is_default: e.target.checked })}
                  />
                  <span>Offer this one first when sending</span>
                </label>
                <p className="small subtle" style={{ margin: 0 }}>
                  Sent as plain text in a plain wrapper — no logo, no images, no tracking. A
                  message asking somebody to write back, dressed up to look marketed, is
                  indistinguishable from the phishing it will be taken for.
                </p>
              </div>
            </Modal>

            <ConfirmModal
              open={!!confirmDelete}
              onClose={() => setConfirmDelete(null)}
              onConfirm={remove}
              busy={busy}
              confirmLabel="Delete letter"
              title={`Delete “${confirmDelete?.name}”?`}
              body={
                <p>
                  Letters already sent under it keep their own copy of the words, so nothing
                  already sent changes and nothing is unsent. Only this template goes.
                </p>
              }
            />
          </div>
        )}
      </QueryState>
    </PageHeader>
  )
}
